/**
 * g711_decoder.ts
 *
 * ITU-T G.711 companding tables (A-law and µ-law) → signed 16-bit PCM.
 *
 * The A-law implementation is a verbatim port of the reference decoder in
 * `scripts/verify-audio.mjs:107-122`, which is itself Sun's `g711.c`
 * `alaw2linear`. That script is the acceptance harness for the audio path, so
 * keeping the two byte-identical is what lets a `.wav` it produces be compared
 * against browser output sample-for-sample.
 *
 * Both tables are built once at module scope (256 entries each, 512 bytes) and
 * are *transferred into the AudioWorklet* via `processorOptions` rather than
 * rebuilt there. The worklet runs in a separate realm and can close over
 * nothing, so the alternative would be duplicating this code inside the
 * processor source string — which is exactly how the two copies drift apart.
 *
 * Wire mapping (relay `internal/relay/audio_framer.go:18-21`, derived from the
 * JT/T1078 payload type at `internal/relay/tcp_server.go:253-256`):
 *   MSG_AUDIO_INIT byte 1 == 0 → PCMA / A-law  (JT/T1078 PT 8)
 *   MSG_AUDIO_INIT byte 1 == 1 → PCMU / µ-law  (JT/T1078 PT 0)
 *
 * µ-law is unreachable on today's fleet but is one device-side config change
 * away, and the failure mode of ignoring it is loud garbage rather than
 * silence — so both tables ship.
 */

/** Codec identifiers as they appear in MSG_AUDIO_INIT byte 1. */
export const G711_CODEC_ALAW = 0;
export const G711_CODEC_ULAW = 1;

export type G711CodecName = 'g711a' | 'g711u';

/** Human-readable name for a MSG_AUDIO_INIT codec byte, or null if unknown. */
export function g711CodecName(codec: number): G711CodecName | null {
  if (codec === G711_CODEC_ALAW) return 'g711a';
  if (codec === G711_CODEC_ULAW) return 'g711u';
  return null;
}

/**
 * ITU-T G.711 A-law → linear PCM16. Output range ±32256.
 * Verbatim port of `verify-audio.mjs:107-119`.
 *
 * (The handoff's §2.1 quotes ±32124 for this table; that is µ-law's peak.
 * A-law's is ±32256 — `0xAA` decodes to `+32256`. The code is the verbatim
 * port it asks for; only the quoted constant differs.)
 */
function alawToPcm16(aVal: number): number {
  const a = aVal ^ 0x55;
  let t = (a & 0x0f) << 4;
  const seg = (a & 0x70) >> 4;

  if (seg === 0) t += 8;
  else if (seg === 1) t += 0x108;
  else {
    t += 0x108;
    t <<= seg - 1;
  }
  return a & 0x80 ? t : -t;
}

/**
 * ITU-T G.711 µ-law → linear PCM16. Output range ±32124.
 * Sun `g711.c` `ulaw2linear`: BIAS 0x84, quantisation mask 0x0f, segment mask
 * 0x70. The input is complemented first, as the standard stores µ-law inverted.
 */
function ulawToPcm16(uValRaw: number): number {
  const uVal = ~uValRaw & 0xff;
  let t = ((uVal & 0x0f) << 3) + 0x84;
  t <<= (uVal & 0x70) >> 4;
  return uVal & 0x80 ? 0x84 - t : t - 0x84;
}

function buildTable(fn: (v: number) => number): Int16Array {
  const table = new Int16Array(256);
  for (let i = 0; i < 256; i++) table[i] = fn(i);
  return table;
}

/** 256-entry A-law → PCM16 lookup table. Built once. */
export const ALAW_TABLE: Int16Array = buildTable(alawToPcm16);

/** 256-entry µ-law → PCM16 lookup table. Built once. */
export const ULAW_TABLE: Int16Array = buildTable(ulawToPcm16);

/** The decode table for a MSG_AUDIO_INIT codec byte. Unknown codecs fall back
 *  to A-law, which is what the wire has always carried — see
 *  `HANDOFF-web-player-audio.md` §1. */
export function tableForCodec(codec: number): Int16Array {
  return codec === G711_CODEC_ULAW ? ULAW_TABLE : ALAW_TABLE;
}
